import type { MailLinkDto } from "../../../services/mailLinkService";
import type { DocumentDto } from "../../../services/documentService";

/**
 * EINE DATEI, EINE UNTERHALTUNG (Rücksprache 06.10.2026).
 *
 * „Die Datei soll nur einmal angezeigt werden und in ihr sind ja die
 * Fassungen" — und „wenn im Vorgang Teile einer Unterhaltung sind, dann muss
 * es zusammengefasst werden in einer Unterhaltung".
 *
 * Gemessen auf PROD am 06.10.2026: 249 Gruppen gleichnamiger Mail-Anhänge je
 * Vorgang (571 Dokumente) — jede Antwort im Verlauf trägt die Anlage erneut,
 * und jede abgelegte Mail legt sie noch einmal in den Vorgang. Dazu 456
 * Unterhaltungen mit mehreren Mails (1.606 Mails), die bisher als einzelne
 * Karten untereinander standen.
 *
 * Beides ist eine reine ANZEIGE-Ableitung: gespeichert bleibt jede Mail und
 * jedes Dokument wie bisher. Die Karte zeigt das jüngste Stück; die übrigen
 * stehen aufklappbar darin. So greift es auch für den Bestand, ohne eine
 * Zeile umzuschreiben.
 *
 * „Wenn die Unterhaltung im Verlauf verändert wurde, dann nicht": Was jemand
 * im Verlauf eigens angefasst hat, bleibt eine eigene Karte —
 *  - in ein anderes To-do gezogen → anderer Strang, eigene Gruppe;
 *  - kommentiert → `verankert`. Ein Kommentar hängt an GENAU diesem Stück;
 *    zusammengefaltet stünde er unsichtbar in der Karte eines anderen.
 */

export interface Zusammengefasst<T> {
    /** Was als Karte erscheint — je Gruppe der Vertreter, Reihenfolge der Eingabe. */
    sichtbar: T[];
    /** Vertreter-Id → die ausgeblendeten übrigen Stücke, jüngstes zuerst. */
    weitereVon: Map<number, T[]>;
}

function zeit(iso?: string | null): number {
    if (!iso) return 0;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
}

/** Jüngstes zuerst, bei Gleichstand die höhere Id (die später angelegte). */
function juengerZuerst<T extends { id: number }>(at: (x: T) => number) {
    return (a: T, b: T) => at(b) - at(a) || b.id - a.id;
}

/**
 * Gruppiert nach `schluessel` (null = steht allein). Vertreter jeder Gruppe ist
 * das jüngste Stück; verankerte Stücke bleiben stehen, nur die übrigen werden
 * in den Vertreter gefaltet.
 */
function fasseZusammen<T extends { id: number }>(
    liste: readonly T[],
    schluessel: (x: T) => string | null,
    at: (x: T) => number,
    verankert: ReadonlySet<number>,
): Zusammengefasst<T> {
    const gruppen = new Map<string, T[]>();
    for (const x of liste ?? []) {
        const k = schluessel(x);
        if (k == null) continue;
        const g = gruppen.get(k);
        if (g) g.push(x);
        else gruppen.set(k, [x]);
    }
    const ausgeblendet = new Set<number>();
    const weitereVon = new Map<number, T[]>();
    for (const g of gruppen.values()) {
        if (g.length < 2) continue;
        const sortiert = [...g].sort(juengerZuerst(at));
        const vertreter = sortiert[0]!;
        const weitere = sortiert.slice(1).filter((x) => !verankert.has(x.id));
        if (weitere.length === 0) continue;
        for (const x of weitere) ausgeblendet.add(x.id);
        weitereVon.set(vertreter.id, weitere);
    }
    if (ausgeblendet.size === 0) return { sichtbar: [...(liste ?? [])], weitereVon };
    return { sichtbar: (liste ?? []).filter((x) => !ausgeblendet.has(x.id)), weitereVon };
}

/**
 * Die Mails einer Unterhaltung im selben Strang → EINE Karte.
 *
 * Schlüssel ist die Graph-`conversationId` plus der Strang (To-do bzw. „am
 * Vorgang"). Ohne `conversationId` (Bestand, lokale `.eml`) steht die Mail
 * allein. Erwartet die Liste NACH {@link gruppiereVorgangsMails} — dieselbe
 * Mail aus zwei Postfächern ist dann schon eine.
 */
export function fasseUnterhaltungenZusammen(
    mails: readonly MailLinkDto[],
    verankert: ReadonlySet<number> = new Set(),
): Zusammengefasst<MailLinkDto> {
    return fasseZusammen(
        mails,
        (m) => {
            const conv = (m.conversationId ?? "").trim();
            if (!conv) return null;
            return `${m.todoId != null ? `t${m.todoId}` : "v"}|${conv}`;
        },
        (m) => zeit(m.receivedAt ?? m.linkedAt),
        verankert,
    );
}

/** Dateiname als Vergleichsschlüssel: ohne Groß-/Kleinschreibung und Mehrfach-Leerzeichen. */
export function dateiSchluessel(name: string | null | undefined): string | null {
    const n = String(name ?? "")
        .normalize("NFC")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    return n.length > 0 ? n : null;
}

/**
 * Gleichnamige Dateien im selben Strang → EINE Datei, die übrigen sind ihre
 * früheren Fassungen. „Gleichnamig“ schließt die früheren Namen einer im
 * Editor umbenannten Datei ein (`fruehereNamen`).
 *
 * Ausgenommen bleiben (`ausnehmen`) die abgelegten Mails (`.eml`/`.msg`, die
 * der Verlauf als Mail zeigt) und Dokumente, die der Vorgang nur zusätzlich
 * anzeigt (`gast`): Die gehören woanders hin und tragen dort ihre eigene
 * Geschichte.
 */
export function fasseFassungenZusammen(
    dokumente: readonly DocumentDto[],
    opts: { ausnehmen?: ReadonlySet<number>; verankert?: ReadonlySet<number> } = {},
): Zusammengefasst<DocumentDto> {
    const ausnehmen = opts.ausnehmen ?? new Set<number>();
    const verankert = opts.verankert ?? new Set<number>();
    const liste = dokumente ?? [];

    // Eine Datei hat mehrere Namen, sobald sie im Editor unter neuem Namen
    // gespeichert wurde (User 07.10.2026): „Angebot Provinzial 03.2026.pdf“
    // heißt jetzt „20260331 Angebot“. Die gleichnamige Kopie aus einer anderen
    // Mail stand vorher als frühere Fassung in ihr — und stünde ohne die
    // früheren Namen plötzlich als zweite Datei daneben. Deshalb gehört
    // zusammen, was im selben Strang IRGENDEINEN Namen teilt (Union-Find).
    const eltern = new Map<number, number>();
    const wurzel = (id: number): number => {
        let w = id;
        while (eltern.get(w) !== w) w = eltern.get(w)!;
        eltern.set(id, w);
        return w;
    };
    const ersterMitName = new Map<string, number>();
    for (const d of liste) {
        if (ausnehmen.has(d.id) || d.gast) continue;
        eltern.set(d.id, d.id);
        const strang = d.todoId != null ? `t${d.todoId}` : "v";
        for (const roh of [d.fileName, ...(d.fruehereNamen ?? [])]) {
            const name = dateiSchluessel(roh);
            if (!name) continue;
            const k = `${strang}|${name}`;
            const erster = ersterMitName.get(k);
            if (erster == null) ersterMitName.set(k, d.id);
            else eltern.set(wurzel(d.id), wurzel(erster));
        }
    }
    const gruppen = new Map<number, DocumentDto[]>();
    for (const d of liste) {
        if (!eltern.has(d.id)) continue;
        const w = wurzel(d.id);
        const g = gruppen.get(w);
        if (g) g.push(d);
        else gruppen.set(w, [d]);
    }

    // Vertreter ist die BEARBEITETE Datei (sie trägt die Fassungen), sonst die jüngste.
    const at = (d: DocumentDto) => zeit(d.uploadedAt);
    const juenger = juengerZuerst<DocumentDto>(at);
    const ausgeblendet = new Set<number>();
    const weitereVon = new Map<number, DocumentDto[]>();
    for (const g of gruppen.values()) {
        if (g.length < 2) continue;
        const sortiert = [...g].sort((a, b) => Number(!!b.bearbeitet) - Number(!!a.bearbeitet) || juenger(a, b));
        const vertreter = sortiert[0]!;
        const weitere = sortiert.slice(1).filter((x) => !verankert.has(x.id));
        if (weitere.length === 0) continue;
        for (const x of weitere) ausgeblendet.add(x.id);
        weitereVon.set(vertreter.id, weitere);
    }
    if (ausgeblendet.size === 0) return { sichtbar: [...liste], weitereVon };
    return { sichtbar: liste.filter((x) => !ausgeblendet.has(x.id)), weitereVon };
}
